import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface Purchase {
  id: string;
  purchaseNumber: string;
  supplierId?: string;
  branchId: string;
  totalAmount: number;
  taxAmount: number;
  netAmount: number;
  status: "pending" | "received" | "cancelled";
  receivedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  supplierName?: string;
  branch?: {
    id: string;
    name: string;
  };
  supplier?: {
    id: string;
    name: string;
  };
}

export interface PurchaseItem {
  id: string;
  purchaseId: string;
  productId: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  receivedQuantity: number;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: string;
    name: string;
    sku: string;
  };
}

export interface CreatePurchaseData {
  supplierId?: string;
  branchId: string;
  totalAmount: number;
  taxAmount?: number;
  netAmount: number;
  status?: "pending" | "received" | "cancelled";
  notes?: string;
  items: {
    productId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }[];
}

export interface UpdatePurchaseData {
  supplierId?: string;
  status?: "pending" | "received" | "cancelled";
  notes?: string;
  items?: {
    productId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    receivedQuantity?: number;
  }[];
}

const purchaseAPI = {
  // Get all purchases with pagination
  getPurchases: async (params?: {
    pageSize?: number;
    pageNo?: number;
    branchId?: string;
    supplierId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: "purchaseNumber" | "supplier" | "branch" | "totalAmount" | "status" | "createdAt";
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ data: Purchase[]; total: number }> => {
    const response = await api.get("/purchases", { params });
    const { data, total } = parsePaginatedList<Purchase>(response.data.data);
    return { data, total };
  },

  // Get purchase by ID
  getPurchaseById: async (id: string): Promise<Purchase> => {
    const response = await api.get(`/purchases/${id}`);
    return response.data.data;
  },

  // Create new purchase
  createPurchase: async (data: CreatePurchaseData): Promise<Purchase> => {
    const response = await api.post("/purchases", data);
    return response.data.data;
  },

  // Update purchase
  updatePurchase: async (
    id: string,
    data: UpdatePurchaseData
  ): Promise<Purchase> => {
    const response = await api.put(`/purchases/${id}`, data);
    return response.data.data;
  },

  // Delete purchase
  deletePurchase: async (id: string): Promise<void> => {
    await api.delete(`/purchases/${id}`);
  },

  // Receive purchase (update status to received)
  receivePurchase: async (id: string): Promise<Purchase> => {
    const response = await api.put(`/purchases/${id}`, { status: "received" });
    return response.data.data;
  },

  // Cancel purchase (update status to cancelled)
  cancelPurchase: async (id: string): Promise<Purchase> => {
    const response = await api.put(`/purchases/${id}`, { status: "cancelled" });
    return response.data.data;
  },

  /** Purchase return → creates debit note + reverses stock */
  returnPurchase: async (
    id: string,
    payload?: { reason?: string; returnAmount?: number }
  ): Promise<{ purchase: Purchase; debitNote: { debitNoteNumber: string } }> => {
    const response = await api.post(`/purchases/${id}/return`, payload || {});
    return response.data.data;
  },

  // Get purchases by salesPoint
  getPurchasesBySalesPoint: async (
    salesPointId: string
  ): Promise<{ data: Purchase[]; total: number }> => {
    const response = await api.get(`/purchases/salesPoint/${salesPointId}`);
    return {
      data: response.data.data,
      total: response.data.data.length,
    };
  },

  // Get purchase report
  getPurchaseReport: async (params?: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
  }): Promise<any> => {
    const response = await api.get("/purchases/report", { params });
    return response.data.data;
  },
};

export default purchaseAPI;
